import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight, ExternalLink, Shield, type LucideIcon } from "lucide-react";
import { usePublicFabbriche, usePublicMarchi, usePublicServizi } from "@/hooks/usePublicEntities";
import { getFabbricaIcon, getServizioIcon, getIconByName } from "@/lib/icon-registry";
import EntityIcon from "@/components/EntityIcon";

interface MegaMenuProps {
  isOpen: boolean;
  menuType: 'FABBRICHE' | 'PROGETTI' | 'MARCHI' | null;
  onClose: () => void;
  onMouseEnter: () => void;
}

type MenuVariant = "logo" | "icon";

type MenuItem = {
  title: string;
  href: string;
  image: string;            // logo / icon image url
  iconComponent?: LucideIcon; // for PROGETTI (Lucide)
  heroImage?: string | null;
  eyebrow: string;
  variant: MenuVariant;
  external?: boolean;
};

const MegaMenu = ({ isOpen, menuType, onClose, onMouseEnter }: MegaMenuProps) => {
  const { data: fabbriche } = usePublicFabbriche();
  const { data: marchi } = usePublicMarchi();
  const { data: servizi } = usePublicServizi();

  if (!isOpen || !menuType) return null;

  let title = "";
  let description = "";
  let allHref = "/";
  let allLabel = "Esplora tutto";
  let items: MenuItem[] = [];

  if (menuType === "FABBRICHE") {
    title = "Le Nostre Fabbriche";
    description = "Scopri i nostri impianti produttivi dove tradizione e innovazione si incontrano";
    allHref = "/fabbriche";
    items = (fabbriche || []).map((f) => ({
      title: f.nome_breve || f.nome,
      href: `/fabbriche/${f.slug}`,
      image: f.icona_url || "/placeholder.svg",
      iconComponent: getIconByName(f.icona_url) || getFabbricaIcon(f.slug),
      heroImage: f.hero_image_url,
      eyebrow: "Fabbrica",
      variant: "icon",
    }));
  } else if (menuType === "PROGETTI") {
    title = "Progetti e Servizi";
    description = "Soluzioni personalizzate per ogni esigenza del tuo business";
    allHref = "/progetti";
    items = (servizi || []).map((s) => ({
      title: s.nome,
      href: `/progetti/${s.slug}`,
      image: s.icona_url || "",
      iconComponent: getIconByName(s.icona_url) || getServizioIcon(s.slug),
      heroImage: s.hero_image_url || "/lovable-uploads/PROGETTI.png",
      eyebrow: "Servizio",
      variant: "icon",
    }));
  } else {
    title = "I Nostri Marchi";
    description = "Marchi storici e innovativi che rappresentano l'eccellenza toscana";
    allHref = "/marchi";
    allLabel = "Tutti i marchi";
    const menuMarchi = (marchi || []).filter((m) => m.show_in_menu && m.tipo !== "difesa");
    items = menuMarchi.map((m) => {
      const isExternal = !m.has_internal_page && !!m.website_url;
      const logoValue = m.carousel_logo_url || m.logo_url || "";
      const maybeIcon = getIconByName(logoValue);
      return {
        title: m.nome,
        href: isExternal ? (m.website_url as string) : `/brands/${m.slug}`,
        image: logoValue || "/placeholder.svg",
        iconComponent: maybeIcon || undefined,
        heroImage: m.hero_image_url,
        eyebrow: m.tipo === "partner" ? "Marchio Partner" : "Marchio Proprio",
        variant: "logo",
        external: isExternal,
      };
    });
  }

  const renderCard = (item: MenuItem) => {
    const Arrow = item.external ? ExternalLink : ArrowUpRight;
    const hero = item.heroImage || "";
    // Mostra il nome sotto per Fabbriche e Progetti (non per Marchi: il logo basta)
    const showName = menuType === "FABBRICHE" || menuType === "PROGETTI";

    return (
      <div className="group relative aspect-square w-full overflow-hidden border border-border bg-card transition-all duration-500 hover:border-terracotta hover:scale-[1.01] hover:z-10">
        {/* Hero al hover */}
        {hero && (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center opacity-0 transition-opacity duration-500 group-hover:opacity-100"
              style={{ backgroundImage: `url(${hero})` }}
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-black/55 opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
          </>
        )}

        {/* Contenuto centrale: logo/icona + (eventuale) nome — coerente con BrandWall/ProjectsGrid */}
        {showName ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-3 transition-all duration-500 group-hover:scale-105">
            {item.iconComponent ? (
              <item.iconComponent
                className={`h-16 w-16 md:h-20 md:w-20 text-foreground/70 transition-[color,filter] duration-500 ${hero ? "group-hover:text-white group-hover:[filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.6))]" : ""}`}
                strokeWidth={1.5}
              />
            ) : (
              <EntityIcon
                value={item.image}
                fallbackIcon={Shield}
                alt={item.title}
                className={`h-16 w-16 md:h-20 md:w-20 text-foreground/70 transition-[color,filter] duration-500 ${hero ? "group-hover:text-white group-hover:[filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.6))]" : ""}`}
                imgClassName={`h-16 md:h-20 w-auto max-w-[80%] object-contain transition-[filter] duration-500 ${hero ? "group-hover:[filter:brightness(0)_invert(1)]" : ""}`}
              />
            )}
            <h4 className={`mt-3 sm:mt-4 font-display text-base sm:text-lg leading-tight text-center line-clamp-2 transition-colors duration-500 ${hero ? "text-foreground/85 group-hover:text-white" : "text-foreground/85"}`}>
              {item.title}
            </h4>
          </div>
        ) : (
          // MARCHI: logo grande centrato senza nome
          <div className="absolute inset-3 sm:inset-4 flex items-center justify-center transition-all duration-500 group-hover:scale-105">
            {item.iconComponent ? (
              <item.iconComponent
                className={`w-full h-full max-w-[70%] max-h-[70%] text-foreground/70 transition-[color,filter] duration-500 ${hero ? "group-hover:text-white group-hover:[filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.6))]" : ""}`}
                strokeWidth={1.5}
              />
            ) : (
              <EntityIcon
                value={item.image}
                fallbackIcon={Shield}
                alt={item.title}
                className={`w-full h-full max-w-[70%] max-h-[70%] text-foreground/70 transition-[color,filter] duration-500 ${hero ? "group-hover:text-white group-hover:[filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.6))]" : ""}`}
                imgClassName={`w-full h-full object-contain transition-[filter] duration-500 ${hero ? "group-hover:[filter:brightness(0)_invert(1)]" : ""}`}
              />
            )}
          </div>
        )}

        {/* Eyebrow top */}
        <div className="absolute inset-x-0 top-0 px-3 pt-2.5 opacity-0 -translate-y-1 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
          <p
            className={`text-[10px] uppercase tracking-[0.22em] truncate ${hero ? "text-terracotta-soft" : "text-terracotta"}`}
            style={hero ? { textShadow: "0 1px 2px rgba(0,0,0,0.6)" } : undefined}
          >
            {item.eyebrow}
          </p>
        </div>

        {/* Solo freccia bottom-right */}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-end px-3 pb-2.5 opacity-0 translate-y-1 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
          <Arrow
            className={`h-5 w-5 shrink-0 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${hero ? "text-white" : "text-terracotta"}`}
            style={hero ? { filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))" } : undefined}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className="absolute left-0 right-0 top-full -mt-px z-[140] bg-background border-t border-b border-border shadow-2xl animate-fade-in"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onClose}
    >
      <div className="container mx-auto px-6 py-5">
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-[0.22em] text-terracotta mb-1">{menuType === 'MARCHI' ? 'Marchi' : menuType === 'FABBRICHE' ? 'Fabbriche' : 'Progetti'}</p>
          <h3 className="text-lg md:text-xl font-display font-bold text-foreground mb-1 leading-tight">{title}</h3>
          <p className="text-xs md:text-sm text-muted-foreground">{description}</p>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {items.map((item, index) =>
            item.external ? (
              <a
                key={`${item.href}-${index}`}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                aria-label={item.title}
              >
                {renderCard(item)}
              </a>
            ) : (
              <Link
                key={`${item.href}-${index}`}
                to={item.href}
                onClick={onClose}
                aria-label={item.title}
              >
                {renderCard(item)}
              </Link>
            )
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-border text-center">
          <Link
            to={allHref}
            onClick={onClose}
            className="inline-flex items-center gap-2 text-terracotta hover:text-terracotta/80 font-medium text-sm uppercase tracking-[0.2em] transition-all hover:gap-3"
          >
            <span>{allLabel}</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MegaMenu;
